/**
 * Mapbox Marker
 * 
 * 如果不同类型的点具有相同的经纬度，就会重合在一起
 * 前端会手动去掉重合的点，点显示优先级如下：
 * 
 *  买房 > 租房 > 售出 > 估价
 * 
 * 比如: 买房与租房如果存在经纬度重合，就不会显示租房
 * 
 * ==== 数据聚合 ====
 *  如果用接口返回的 md5 对数据进行聚合
 *  还是存在经纬度重合的情况
 *  所以在初始化时，新增了一个字段 uniquePointKey(lng+lat)
 *  来处理点重合的问题：
 * 
 *   1）将经纬度保留5位小数
 *   2) 将处理后的经纬度相同的数据的points数组合并
 * 
 * @Author: Focci
 * @Date: 2023-07-09 19:07:25
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-09 19:07:25
 */

'use client'

import PropTypes from 'prop-types'
import { map, isArray } from 'lodash-es'
import { useMapState } from '@comp/mapbox'
import { useCallback, useEffect, useRef, useState } from 'react'
import EstimateMarkers from './EstimateMarkers'
import PropertyMarkers from './PropertyMarkers'
import RentalMarkers from './RentalMarkers'
import SoldMarkers from './SoldMarkers'
import SchoolMarkers from './SchoolMarkers'
import StateHouseMarker from './StateHouseMarker'
import RuralMarkers from './RuralMarkers'
import CommercialForLeaseMarkers from './CommercialForLeaseMarkers'
import CommercialForSaleMarkers from './CommercialForSaleMarkers'
import BusStopMarker from './BusStopMarker'
import { lngLatStr } from './lib'

export default function MapboxMarker({
  apiParam = null,
  ...markerProps
}) {
  const mapboxState = useMapState()
  const allMarkers = useRef({})

  // 租房在地图上不显示的点
  const [rentalExclude, setRentalExclude] = useState([])
  // 售出在地图上不显示的点
  const [soldExclude, setSoldExclude] = useState([])
  // 估价在地图上不显示的点
  const [estimateExclude, setEstimateExclude] = useState([])

  const getExcludeData = useCallback((props = []) => {
    const tmp = []
    const amc = allMarkers.current
    
    props.forEach((prop) => {
      prop && isArray(amc[prop]) && tmp.push(...amc[prop])
    })

    return tmp
  }, [])
  
  const setMakerDisplay = useCallback(() => {
    const { property, rental, sold } = mapboxState
    const p = property ? 'property' : null
    const r = rental ? 'rental' : null
    const s = sold ? 'sold' : null

    setRentalExclude(getExcludeData([p]))
    setSoldExclude(getExcludeData([p, r]))
    setEstimateExclude(getExcludeData([p, r, s]))
  }, [getExcludeData, mapboxState])

  const handleDataUpdate = useCallback((d, type) => {
    allMarkers.current[type] = map(d, lngLatStr)
    setMakerDisplay()
  }, [setMakerDisplay])

  useEffect(() => { setMakerDisplay() }, [setMakerDisplay])

  return (
    <>
      {mapboxState.school && (
        <SchoolMarkers apiParam={apiParam} {...markerProps} />
      )}
      {mapboxState.property && (
        <PropertyMarkers
          onDataUpdate={handleDataUpdate}
          apiParam={apiParam}
          {...markerProps}
        />
      )}
      {mapboxState.rental && (
        <RentalMarkers
          onDataUpdate={handleDataUpdate}
          exclude={rentalExclude}
          apiParam={apiParam}
          {...markerProps}
        />
      )}
      {mapboxState.sold && (
        <SoldMarkers
          onDataUpdate={handleDataUpdate}
          exclude={soldExclude}
          apiParam={apiParam}
          {...markerProps}
        />
      )}
      {mapboxState.estimate && (
        <EstimateMarkers
          onDataUpdate={handleDataUpdate}
          exclude={estimateExclude}
          apiParam={apiParam}
          {...markerProps}
        />
      )}
      {mapboxState.layers.display.indexOf('governmentHousing') > -1 && (
        <StateHouseMarker
          onDataUpdate={handleDataUpdate}
          exclude={rentalExclude}
          apiParam={apiParam}
          {...markerProps}
        />
      )}
      {mapboxState.rural && (
        <RuralMarkers
          onDataUpdate={handleDataUpdate}
          exclude={rentalExclude}
          apiParam={apiParam}
          {...markerProps}
        />
      )}
      {mapboxState.commercialForSale && (
        <CommercialForLeaseMarkers
          onDataUpdate={handleDataUpdate}
          exclude={rentalExclude}
          apiParam={apiParam}
          {...markerProps}
        />
      )}
      {mapboxState.commercialForLease && (
        <CommercialForSaleMarkers
          onDataUpdate={handleDataUpdate}
          exclude={estimateExclude}
          apiParam={apiParam}
          {...markerProps}
        />
      )}
      {/* 公交站点 */}
      {mapboxState.busStops && (
        <BusStopMarker {...markerProps} />
      )}
    </>
  )
}

MapboxMarker.propTypes = {
  apiParam: PropTypes.object,
}
